/**
 * UdaRequiredField
 *
 * The mandatory UDA picker shown at the top of every WMS create form. A record (and
 * every work picked inside the form) belongs to the UDA chosen here.
 *
 *   - State / regional users choose it. It starts on the UDA picked in the WMS header,
 *     and stays empty under "All UDAs" so the user has to choose.
 *   - A UDA-pinned user sees their own UDA, read-only; the server scopes them to it
 *     whatever is sent.
 *   - `locked` shows the value read-only, e.g. when editing a record whose UDA is fixed.
 *
 * The parent owns the value; this component only fills the default once (when the
 * value is still empty) and reports changes.
 */
import { useEffect } from 'react';
import { useWmsUdaScope } from './WmsUdaScope';

interface UdaRequiredFieldProps {
  value: number | null | undefined;
  onChange: (udaId: number | null) => void;
  /** Show the value read-only (editing a record whose UDA is fixed). */
  locked?: boolean;
  /** Validation message from the parent form, e.g. "Select a UDA". */
  error?: string;
  label?: string;
  /** Shown under the field, e.g. what changing it does to picked works. */
  hint?: string;
  className?: string;
}

export function UdaRequiredField({
  value, onChange, locked = false, error, label = 'UDA', hint, className = '',
}: UdaRequiredFieldProps) {
  const scope = useWmsUdaScope();

  // Fill an empty value with the header UDA (state user) or the user's own UDA. Runs
  // again whenever the value is empty, so a form that resets its state on reopen gets
  // the default back.
  useEffect(() => {
    if (value == null && scope.udaId != null) onChange(scope.udaId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.udaId, value]);

  const readOnly = locked || !scope.canChoose;
  const name = value != null
    ? (scope.udas.find((u) => u.uda_id === Number(value))?.uda_nm || `UDA #${value}`)
    : '';

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} <span className="text-red-500">*</span>
      </label>
      {readOnly ? (
        <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-700 flex items-center gap-2">
          <i className="ri-government-line text-gray-400" />
          {name || <span className="text-gray-400 italic">No UDA</span>}
          <i className="ri-lock-line text-gray-300 ml-auto" title="Fixed" />
        </div>
      ) : (
        <select
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
          disabled={scope.loading}
          className={`w-full px-3 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300 ${
            error ? 'border-red-400' : 'border-gray-300'
          }`}
        >
          <option value="">{scope.loading ? 'Loading UDAs...' : 'Select UDA'}</option>
          {scope.udas.map((u) => (
            <option key={u.uda_id} value={u.uda_id}>{u.uda_nm}</option>
          ))}
        </select>
      )}
      {error ? (
        <p className="text-xs text-red-500 mt-1">{error}</p>
      ) : hint ? (
        <p className="text-xs text-gray-400 mt-1">{hint}</p>
      ) : null}
    </div>
  );
}

export default UdaRequiredField;
